<template>

	<!-- ************************************************************************** Data table -->
	<PCard>
		<template #title>
			<div class="flex align-items-center justify-content-between flex-wrap gap-2">
				<div class="flex align-items-center gap-3 flex-wrap">
					<WTitle text="Solar Irradiation Readings" />
					<span
						v-if="option === 'Data'"
						class="text-sm text-color-secondary"
					>
						~{{ total.toLocaleString() }} records
						<span v-if="pages > 0">· ~{{ pages.toLocaleString() }} pages</span>
					</span>
				</div>
				<div class="flex align-items-center gap-2 flex-wrap">
					<div
						v-if="option === 'Data'"
						class="flex align-items-center gap-2"
					>
						<label
							class="text-sm text-color-secondary white-space-nowrap"
							for="solar-show-ts"
						>Timestamp</label>
						<PInputSwitch
							inputId="solar-show-ts"
							v-model="showTimestamp"
						/>
					</div>
					<PButton
						:loading="loading"
						@click="refresh()"
						icon="pi pi-refresh"
						label="Refresh"
						rounded
						size="small"
						text
					/>
					<PSelectButton
						:allowEmpty="false"
						:options="options"
						aria-labelledby="basic"
						v-model="option"
						@change="onOptionChange"
					/>
				</div>
			</div>
		</template>
		<template #content>
			<SolarChart
				v-if="option == 'Chart'"
				:data="chartList"
				:key="chartKey"
			/>
			<PDataTable
				:alwaysShowPaginator="true"
				:first="first"
				:highlightOnSelect="true"
				:lazy="true"
				:loading="loading"
				:resizableColumns="true"
				:rowHover="true"
				:rows="rowsPerPage"
				:rowsPerPageOptions="[50, 100, 200, 500]"
				:showGridlines="true"
				:sortOrder="-1"
				:stripedRows="true"
				:style="paginatorStyle"
				:totalRecords="paginatorTotal"
				:value="list"
				currentPageReportTemplate="{first}–{last} of ~{totalRecords}"
				data-key="id"
				paginator
				paginatorPosition="both"
				paginatorTemplate="FirstPageLink PrevPageLink NextPageLink LastPageLink JumpToPageInput RowsPerPageDropdown CurrentPageReport"
				size="small"
				sortField="id"
				v-if="option == 'Data'"
				@page="onPage($event)"
				class="readings-table"
			>
				<template #empty>No records found.</template>

				<PColumn
					bodyClass="text-left"
					class="justify-content-center"
					field="id"
					header="ID"
					headerClass="py-3"
					style="width: 25px;"
				></PColumn>
				<PColumn
					v-if="showTimestamp"
					bodyClass="text-left"
					class="justify-content-center"
					field="ts"
					header="Timestamp"
					headerClass="py-3"
					style="width: 160px;"
				></PColumn>
				<PColumn
					bodyClass="text-left"
					class="justify-content-center"
					field="date"
					header="Date"
					headerClass="py-3"
					style="width: 200px;"
				></PColumn>
				<PColumn
					bodyClass="text-left"
					class="justify-content-center"
					field="day"
					header="Day"
					headerClass="py-3"
					style="width: 200px;"
				></PColumn>
				<PColumn
					bodyClass="text-left text-wrap"
					class="justify-content-center"
					header="Time"
					headerClass="py-3"
					style="width: 120px;"
				>
					<template #body="{ data }">
						{{ formatReadingTime(data) }}
					</template>
				</PColumn>
				<PColumn
					bodyClass="text-left"
					field="value"
					header="GPOAi"
					headerClass="py-3"
					style="width: 200px;"
				></PColumn>
			</PDataTable>
		</template>
	</PCard>
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import BEBroker from '@/brokers/BEBroker'
import Utils from '@/utils/Utils'
import Constants from '@/helpers/Constants'
import SolarChart from '@/components/SolarChart.vue'

interface ReadingsPage {
	rows: any[]
	total: number
	limit: number
	page: number | null
	pages: number
	cursor: number | null
	next_cursor: number | null
	prev_cursor: number | null
	has_more: boolean
	has_prev: boolean
	total_approximate?: boolean
}

const CHART_LIMIT = 2000

export default defineComponent({
	name: 'SolarReadingsView',
	components: {
		SolarChart
	},

	data() {
		return {
			utils: Utils,
			component: 'solar',
			loading: true,
			list: [] as any[],
			chartList: [] as any[],
			chartKey: 0,
			total: 0,
			pages: 0,
			rowsPerPage: 100,
			first: 0,
			page: 1,
			requestCursor: null as number | null,
			requestDirection: 'next' as 'next' | 'prev',
			nextCursor: null as number | null,
			prevCursor: null as number | null,
			hasMore: false,
			hasPrev: false,
			showTimestamp: false,
			options: [
				'Data',
				'Chart'
			],
			option: 'Data'
		}
	},

	computed: {
		/** Approximate total for jump-to-page; cap Next when keyset says no more. */
		paginatorTotal(): number {
			const approx = Math.max(this.total, this.first + this.list.length)
			if (!this.hasMore) {
				return Math.min(approx, this.first + this.list.length)
			}
			return Math.max(approx, this.first + this.rowsPerPage + 1)
		},
		paginatorStyle(): Record<string, string> {
			return {
				'--readings-page-suffix': `" / ${Math.max(1, this.pages || 1)}"`,
			}
		},
	},

	async mounted() {
		Utils.generatePid(Constants.OPID.READ)
		await this.refreshList()
	},

	methods: {
		formatReadingTime(row: { ts?: number | string; hour?: string }) {
			const raw = row?.ts
			if (raw != null && raw !== '') {
				const ms = Number(raw)
				if (!Number.isNaN(ms) && ms > 0) {
					const d = new Date(ms)
					const hh = String(d.getHours()).padStart(2, '0')
					const mm = String(d.getMinutes()).padStart(2, '0')
					return `${hh}:${mm}`
				}
			}
			return row?.hour || '—'
		},

		async onOptionChange() {
			if (this.option === 'Chart') {
				await this.loadChartData()
			} else {
				await this.goFirst()
			}
		},

		async refresh() {
			if (this.option === 'Chart') {
				await this.loadChartData()
			} else {
				await this.goFirst()
			}
		},

		async onPage(event: { first: number; rows: number; page: number }) {
			const rowsChanged = event.rows !== this.rowsPerPage
			const newPage = event.page + 1
			const delta = newPage - this.page

			if (rowsChanged) {
				this.rowsPerPage = event.rows
				await this.goFirst()
				return
			}

			if (newPage <= 1) {
				await this.goFirst()
				return
			}

			// Adjacent next/prev: prefer keyset cursors.
			if (delta === 1 && this.hasMore && this.nextCursor != null) {
				this.requestCursor = this.nextCursor
				this.requestDirection = 'next'
				this.page = this.page + 1
				this.first = (this.page - 1) * this.rowsPerPage
				await this.refreshList()
				return
			}
			if (delta === -1 && this.hasPrev && this.prevCursor != null) {
				this.requestCursor = this.prevCursor
				this.requestDirection = 'prev'
				this.page = Math.max(1, this.page - 1)
				this.first = (this.page - 1) * this.rowsPerPage
				await this.refreshList()
				return
			}

			// Jump (JumpToPageInput / Last): OFFSET via page number.
			if (delta !== 0) {
				await this.goToPage(newPage)
			}
		},

		async goFirst() {
			this.requestCursor = null
			this.requestDirection = 'next'
			this.page = 1
			this.first = 0
			await this.refreshList()
		},

		async goToPage(pageNum: number) {
			const maxPage = Math.max(1, this.pages || 1)
			const target = Math.min(Math.max(1, pageNum), maxPage)
			if (target <= 1) {
				await this.goFirst()
				return
			}
			this.requestCursor = null
			this.requestDirection = 'next'
			this.page = target
			this.first = (target - 1) * this.rowsPerPage
			await this.refreshList()
		},

		async refreshList() {
			this.loading = true
			try {
				const args: Record<string, string | number> = {
					limit: this.rowsPerPage,
				}
				if (this.requestCursor != null) {
					args.cursor = this.requestCursor
					args.direction = this.requestDirection
				} else if (this.page > 1) {
					args.page = this.page
				}
				const res = await BEBroker.get(this.component, args) as ReadingsPage | any[]
				const page = this.normalizePage(res)
				this.list = page.rows
				this.total = page.total
				this.pages = page.pages
				this.nextCursor = page.next_cursor
				this.prevCursor = page.prev_cursor
				this.hasMore = page.has_more
				this.hasPrev = page.has_prev
				if (page.page != null) {
					this.page = page.page
					this.first = (page.page - 1) * this.rowsPerPage
				}
				if (this.page <= 1) {
					this.hasPrev = false
					this.prevCursor = null
				}
			} catch (e: any) {
				Utils.showErrorMessage(e?.message || 'Could not load solar readings.')
				this.list = []
				this.total = 0
				this.pages = 0
				this.nextCursor = null
				this.prevCursor = null
				this.hasMore = false
				this.hasPrev = false
			} finally {
				this.loading = false
			}
		},

		async loadChartData() {
			this.loading = true
			try {
				const res = await BEBroker.get(this.component, {
					limit: CHART_LIMIT,
					for_chart: 1,
				}) as ReadingsPage | any[]
				const page = this.normalizePage(res)
				this.chartList = page.rows
				this.chartKey += 1
			} catch (e: any) {
				Utils.showErrorMessage(e?.message || 'Could not load chart data.')
				this.chartList = []
			} finally {
				this.loading = false
			}
		},

		normalizePage(res: ReadingsPage | any[] | null): ReadingsPage {
			if (res != null && !Array.isArray(res) && Array.isArray(res.rows)) {
				return {
					rows: res.rows,
					total: Number(res.total) || 0,
					limit: Number(res.limit) || this.rowsPerPage,
					page: res.page ?? this.page,
					pages: Number(res.pages) || 0,
					cursor: res.cursor ?? null,
					next_cursor: res.next_cursor ?? null,
					prev_cursor: res.prev_cursor ?? null,
					has_more: Boolean(res.has_more),
					has_prev: Boolean(res.has_prev),
					total_approximate: res.total_approximate !== false,
				}
			}
			const rows = Array.isArray(res) ? res : []
			return {
				rows,
				total: rows.length,
				limit: this.rowsPerPage,
				page: 1,
				pages: rows.length ? 1 : 0,
				cursor: null,
				next_cursor: null,
				prev_cursor: null,
				has_more: false,
				has_prev: false,
			}
		},
	}
})
</script>

<style scoped>
.readings-table :deep(.p-paginator .p-paginator-page-input) {
	display: inline-flex;
	align-items: center;
	width: auto;
	gap: 0.35rem;
}

.readings-table :deep(.p-paginator .p-paginator-page-input)::after {
	content: var(--readings-page-suffix);
	white-space: nowrap;
	color: var(--text-color-secondary);
	font-size: 0.875rem;
}

.readings-table :deep(.p-paginator .p-paginator-page-input .p-inputnumber-input) {
	width: 5.5rem;
	min-width: 5.5rem;
	text-align: center;
	padding-inline: 0.5rem;
}
</style>
